import { Component, OnInit, Output, EventEmitter, Input, OnChanges } from '@angular/core';
import { forkJoin } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { FipsService } from '../../../../../../../core/fips/fips.service';
import { FipsObject } from '../../../../../../../core/fips/fips-object';
import { CompanyService } from '../../../../../../../core/company/company.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../../app-state';
import { selectUserState, UserState } from '../../../../../../../auth/user-state';
import { AreaSubscription } from '../../../../../../../core/company/area-subscription';

interface GeographyItem {
  id: string;
  name: string;
  abbreviation?: string;
  fips?: string;
  selected: boolean;
  // States only: true when the company subscribes to the state directly (send as filter.states);
  // false = state is shown only because a subscribed CBSA falls in it (send its CBSAs instead).
  isSubscribed?: boolean;
}

@Component({
  standalone: false,
  selector: 'app-geo-filter',
  templateUrl: './geo-filter.component.html',
  styleUrls: ['./geo-filter.component.scss']
})
export class GeoFilterComponent implements OnInit, OnChanges {
  @Input() selectedGeography: {states: string[], counties: string[], msas: string[], derivedStates?: string[]} = {states: [], counties: [], msas: [], derivedStates: []};
  @Output() selectionChange = new EventEmitter<{states: string[], stateLabels: string[], derivedStates: string[], derivedStateLabels: string[], derivedCbsas: string[], counties: string[], msas: string[], msaLabels: string[]}>();

  stateSearchTerm: string = '';
  cbsaSearchTerm: string = '';
  isLoading: boolean = true;

  states: GeographyItem[] = [];
  cbsas: GeographyItem[] = [];

  // Maps a state abbreviation -> the subscribed CBSA fips that fall within it. Used to expand
  // an unsubscribed (derived) state selection into the CBSAs the company is actually entitled to.
  private stateToCbsaFips = new Map<string, string[]>();

  constructor(private fipsService: FipsService,
              private companyService: CompanyService,
              private store: Store<AppState>) { }

  ngOnInit(): void {
    selectUserState(this.store).pipe(
      filter((state: UserState) => !!state.currentCompany),
      take(1),
      switchMap((userState: UserState) =>
        forkJoin([
          this.fipsService.findAllStates().pipe(take(1)),
          this.fipsService.findAllCbsas().pipe(take(1)),
          this.companyService.findAllAreaSubscriptions(userState.currentCompany.uuid).pipe(take(1))
        ])
      )
    ).subscribe(([stateResults, cbsaResults, areaSubscriptions]: [FipsObject[], FipsObject[], AreaSubscription[]]) => {
      const subscribedFips = areaSubscriptions?.map(sub => sub.fips) || [];

      const filteredStates = subscribedFips.length > 0
        ? stateResults.filter(s => subscribedFips.includes(s.fips))
        : stateResults;

      const filteredCbsas = subscribedFips.length > 0
        ? cbsaResults.filter(c => subscribedFips.includes(c.fips))
        : cbsaResults;

      this.cbsas = filteredCbsas.map((c, i) => ({
        id: String(i + 1),
        name: c.name,
        fips: c.fips,
        selected: false
      })).sort((a, b) => a.name.localeCompare(b.name));

      // Build the state -> subscribed-CBSA map from the (already subscription-gated) CBSA list.
      // A CBSA's state(s) come from its name suffix, e.g. "Paducah, KY-IL" -> KY, IL.
      const validAbbrevs = new Set(stateResults.map(s => s.abbreviation).filter(Boolean));
      this.stateToCbsaFips = new Map();
      filteredCbsas.forEach(c => {
        this.parseCbsaStateAbbrevs(c.name, validAbbrevs).forEach(abbr => {
          const list = this.stateToCbsaFips.get(abbr) || [];
          if (!list.includes(c.fips)) list.push(c.fips);
          this.stateToCbsaFips.set(abbr, list);
        });
      });

      // Show every state the company can reach: directly subscribed states PLUS states that
      // contain at least one subscribed CBSA. Tag each so we know how to send it to the API.
      const subscribedStateAbbrevs = new Set(filteredStates.map(s => s.abbreviation));
      const shownAbbrevs = new Set<string>([...subscribedStateAbbrevs, ...this.stateToCbsaFips.keys()]);

      this.states = stateResults
        .filter(s => shownAbbrevs.has(s.abbreviation))
        .map((s, i) => ({
          id: String(i + 1),
          name: s.name,
          abbreviation: s.abbreviation,
          isSubscribed: subscribedStateAbbrevs.has(s.abbreviation),
          selected: false
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      this.updateSelections();
      this.isLoading = false;
    });
  }

  ngOnChanges(): void {
    this.updateSelections();
  }

  private updateSelections(): void {
    if (!this.selectedGeography || (!this.states.length && !this.cbsas.length)) return;

    // A state is re-selected if it was applied as either a subscribed state or a derived one.
    const selectedStateAbbrevs = [
      ...(this.selectedGeography.states || []),
      ...(this.selectedGeography.derivedStates || [])
    ];
    this.states.forEach(state => {
      state.selected = selectedStateAbbrevs.includes(state.abbreviation);
    });
    this.cbsas.forEach(cbsa => {
      cbsa.selected = this.selectedGeography.msas?.includes(cbsa.fips) || false;
    });
  }

  /** Extracts state abbreviation(s) from a CBSA name, e.g. "Paducah, KY-IL" -> ["KY","IL"]. */
  private parseCbsaStateAbbrevs(name: string, validAbbrevs: Set<string>): string[] {
    if (!name) return [];
    const idx = name.lastIndexOf(',');
    if (idx === -1) return [];
    return name.slice(idx + 1)
      .split('-')
      .map(t => t.trim().toUpperCase())
      .filter(t => validAbbrevs.has(t));
  }

  onStateSearchChange(event: any): void {
    this.stateSearchTerm = event.target.value;
  }

  onCbsaSearchChange(event: any): void {
    this.cbsaSearchTerm = event.target.value;
  }

  clearStateSearch(): void {
    this.stateSearchTerm = '';
  }

  clearCbsaSearch(): void {
    this.cbsaSearchTerm = '';
  }

  toggleSelection(item: GeographyItem): void {
    item.selected = !item.selected;
    this.emitSelectionChange();
  }

  private emitSelectionChange(): void {
    const selectedStates = this.states.filter(s => s.selected);

    // Subscribed states are sent to the API as states; derived (unsubscribed) states are
    // expanded into the subscribed CBSAs that fall within them — never the state itself.
    const subscribedStates = selectedStates.filter(s => s.isSubscribed);
    const derivedStates = selectedStates.filter(s => !s.isSubscribed);

    const derivedCbsas: string[] = [];
    derivedStates.forEach(s => {
      (this.stateToCbsaFips.get(s.abbreviation) || []).forEach(fips => {
        if (!derivedCbsas.includes(fips)) derivedCbsas.push(fips);
      });
    });

    this.selectionChange.emit({
      states: subscribedStates.map(s => s.abbreviation),
      stateLabels: subscribedStates.map(s => s.name),
      derivedStates: derivedStates.map(s => s.abbreviation),
      derivedStateLabels: derivedStates.map(s => s.name),
      derivedCbsas,
      counties: [],
      msas: this.cbsas.filter(c => c.selected).map(c => c.fips),
      msaLabels: this.cbsas.filter(c => c.selected).map(c => c.name)
    });
  }

  resetSelections(): void {
    this.states.forEach(state => state.selected = false);
    this.cbsas.forEach(cbsa => cbsa.selected = false);
    this.stateSearchTerm = '';
    this.cbsaSearchTerm = '';
    this.emitSelectionChange();
  }

  getFilteredItems(items: GeographyItem[], searchTerm: string): GeographyItem[] {
    if (!searchTerm) return items;
    return items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
}
